import { Injectable } from '@angular/core';
import {
  MarineInsuranceBillsRepository,
  MarineInsuranceDocumentsRepository,
  MarineInsuranceRepository,
} from '@data-layer/repositories/marine-insurance';
import {
  MarineInsuranceLimitsModel,
  MarineInsuranceManagementModel,
  MarineInsuranceModel,
  MarineInsuranceSearchShipmentModel,
} from '@data-layer/models/marine-insurance';
import { combineLatest, Observable, of, throwError, timer } from 'rxjs';
import { BillNumberModel, DeepPartial, PaginatedResultModel, ShortModel } from '@data-layer/models/common';
import { filter, first, map, switchMap, take } from 'rxjs/operators';
import { MarineInsuranceQuoteFormModel, MarineInsuranceQuoteManagementFormModel } from '@shared/marine-insurance-shared/models';
import { enumMarineInsuranceCommodity, enumMarineInsuranceEntityType } from '@data-layer/enums/marine-insurance';
import { DateTimezoneService } from '@services/date-timezone.service';
import { BillModel, PaymentBillItemPendingModel } from '@data-layer/models/payments';
import { DocumentModel } from '@data-layer/models/documents';
import { IsfModel } from '@data-layer/models/isf';
import { EntrySummaryModel } from '@data-layer/models/entry-summaries';
import { LCLShipmentModel, QuoteModel } from '@data-layer/models/lcl';
import { EntityModel } from '@data-layer/models/entities';
import { enumShippingRole } from '@data-layer/enums/user';
import { EntitiesRepository } from '@data-layer/repositories';
import { enumIsfShipmentCode } from '@data-layer/enums/isf';
import { HttpErrorResponse } from '@angular/common/http';
import { MarineInsuranceCountByStateModel } from '@data-layer/models/management/marine-insurance';
import { enumPaymentProvider } from '@data-layer/enums/payments';
import { DEFAULT_EMPTY_RADIO, DEFAULT_MAX_POLLING_RETRY, DEFAULT_POLLING_TIMEOUT } from '@constants/constants';

@Injectable({ providedIn: 'root' })
export class MarineInsuranceFacade {
  constructor(
    private marineInsuranceRepository: MarineInsuranceRepository,
    private dateTimezoneService: DateTimezoneService,
    private marineInsuranceBillsRepository: MarineInsuranceBillsRepository,
    private marineInsuranceDocumentsRepository: MarineInsuranceDocumentsRepository,
    private entitiesRepository: EntitiesRepository,
  ) {}

  public getShipment(referenceNumber: string, isShowLoader?: boolean): Observable<MarineInsuranceModel> {
    return this.marineInsuranceRepository.getShipment(referenceNumber, isShowLoader);
  }

  public getShipments(params: MarineInsuranceSearchShipmentModel, isShowLoader?: boolean): Observable<MarineInsuranceModel> {
    return this.marineInsuranceRepository.getShipments(params, isShowLoader);
  }

  public getCommodities(isShowLoader?: boolean): Observable<ShortModel[]> {
    // #9043
    return this.marineInsuranceRepository
      .getCommodities(isShowLoader)
      .pipe(map((items: ShortModel[]) => items.filter((item: ShortModel) => item.id !== enumMarineInsuranceCommodity.BOATS_AND_YACHTS)));
  }

  public updateShipment(marineInsurance: MarineInsuranceModel, isShowLoader?: boolean): Observable<MarineInsuranceModel> {
    return this.marineInsuranceRepository.updateShipment(marineInsurance, isShowLoader);
  }

  public deleteShipment(shipmentId: number, isShowLoader?: boolean): Observable<never> {
    return this.marineInsuranceRepository.deleteShipment(shipmentId, isShowLoader);
  }

  public createCertificate(id: number, isShowLoader?: boolean): Observable<MarineInsuranceModel> {
    return this.marineInsuranceRepository.createCertificate(id, isShowLoader);
  }

  public getPendingBills(
    companyId: number,
    referenceNumbers: string[],
    paymentProvider: enumPaymentProvider,
    paymentCodeId: number,
    isCalculateFee: boolean = false,
    coupon?: string,
    isShowLoader?: boolean,
    paymentMethodId?: number,
  ): Observable<PaymentBillItemPendingModel> {
    return this.marineInsuranceBillsRepository.getBills(
      companyId,
      referenceNumbers,
      paymentProvider,
      paymentCodeId,
      isCalculateFee,
      coupon,
      isShowLoader,
      paymentMethodId,
    );
  }

  public createBill(
    companyId: number,
    referenceNumber: string[],
    paymentProvider: enumPaymentProvider,
    paymentCodeId: number,
    billToId: number,
    coupon: string,
    isShowLoader?: boolean,
    paymentMethodId?: number,
  ): Observable<BillModel[]> {
    return this.marineInsuranceBillsRepository.createBill(
      companyId,
      referenceNumber,
      paymentProvider,
      paymentCodeId,
      billToId,
      coupon,
      isShowLoader,
      paymentMethodId,
    );
  }

  public getErrorMessage(error: HttpErrorResponse): string {
    return this.marineInsuranceBillsRepository.getErrorMessage(error);
  }

  public getDocuments(id: number, isShowLoader?: boolean): Observable<DocumentModel> {
    return this.marineInsuranceDocumentsRepository.getDocuments(id, isShowLoader);
  }

  public createEntity(companyId: number, data: Partial<EntityModel>): Observable<EntityModel> {
    return this.entitiesRepository.createEntity(companyId, data);
  }

  public updateEntity(entityId: number, data: Partial<EntityModel>): Observable<EntityModel> {
    return this.entitiesRepository.updateEntity(entityId, data);
  }

  public deleteEntity(entityId: number): Observable<never> {
    return this.entitiesRepository.deleteEntity(entityId);
  }

  public getManagementLimits(isShowLoader?: boolean): Observable<MarineInsuranceLimitsModel> {
    return this.marineInsuranceRepository.getManagementLimits();
  }

  public createManagementEntity(companyId: number, data: Partial<EntityModel>): Observable<EntityModel> {
    return this.entitiesRepository.managementCreate(companyId, data);
  }

  public updateManagementEntity(entityId: number, data: Partial<EntityModel>): Observable<EntityModel> {
    return this.entitiesRepository.managementUpdate(entityId, data);
  }

  public getManagementShipment(id: number, isShowLoader?: boolean): Observable<MarineInsuranceManagementModel> {
    return this.marineInsuranceRepository.getManagementShipment(id, isShowLoader);
  }

  public getManagementShipments(
    params: MarineInsuranceSearchShipmentModel,
    isShowLoader?: boolean,
  ): Observable<PaginatedResultModel<MarineInsuranceManagementModel>> {
    return this.marineInsuranceRepository.getManagementShipments(params, isShowLoader);
  }

  public getManagementCountStats(isShowLoader?: boolean): Observable<MarineInsuranceCountByStateModel[]> {
    return this.marineInsuranceRepository.getManagementCountStats(isShowLoader);
  }

  public updateManagementShipment(
    marineInsurance: DeepPartial<MarineInsuranceManagementModel>,
  ): Observable<MarineInsuranceManagementModel> {
    return this.marineInsuranceRepository.updateManagementShipment(marineInsurance);
  }

  public confirmDraftPayment(marineInsuranceId: number): Observable<PaymentBillItemPendingModel> {
    let lastUpdatedTimestamp: string;

    return this.getManagementShipment(marineInsuranceId).pipe(
      switchMap((shipmentRes: MarineInsuranceManagementModel) => {
        lastUpdatedTimestamp = shipmentRes.priceUpdateTimestamp;
        return this.marineInsuranceRepository.confirmDraftPayment(marineInsuranceId);
      }),
      switchMap(() =>
        timer(0, DEFAULT_POLLING_TIMEOUT).pipe(
          take(DEFAULT_MAX_POLLING_RETRY),
          switchMap(() => this.getManagementShipment(marineInsuranceId)),
          filter((shipmentRes: MarineInsuranceManagementModel) => shipmentRes.priceUpdateTimestamp !== lastUpdatedTimestamp),
          first(),
        ),
      ),
      switchMap((shipment: MarineInsuranceManagementModel) =>
        this.getPendingBills(shipment.companyId, [shipment.referenceNumber], null, null, false),
      ),
    );
  }

  public setManagementFlags(id: number, isShowLoader?: boolean): Observable<MarineInsuranceManagementModel> {
    return this.marineInsuranceRepository.setManagementFlags(id, isShowLoader);
  }

  public setManagementRefund(id: number, isShowLoader?: boolean): Observable<MarineInsuranceManagementModel> {
    return this.marineInsuranceRepository.setManagementRefund(id, isShowLoader);
  }

  public createQuote(
    data: MarineInsuranceQuoteFormModel,
    entity: IsfModel | EntrySummaryModel | QuoteModel | LCLShipmentModel,
    entityType: enumMarineInsuranceEntityType,
    currentCompanyId: number,
  ): Observable<[MarineInsuranceModel, PaymentBillItemPendingModel]> {
    let method: string = null;
    let entityToCreate: EntityModel = null;
    let billNumber: BillNumberModel = { carrier: null, number: null };
    let vesselName: string;
    let voyageNumber: string;

    billNumber = this.getBillData(entity, entityType);

    switch (entityType) {
      case enumMarineInsuranceEntityType.ISF:
        method = 'createIsfInsurance';
        entityToCreate = (entity as IsfModel)?.importer;
        break;
      case enumMarineInsuranceEntityType.ENTRY_SUMMARY:
        method = 'createEsInsurance';
        entityToCreate = (entity as EntrySummaryModel)?.importer;
        vesselName = (entity as EntrySummaryModel)?.vesselName;
        voyageNumber = (entity as EntrySummaryModel)?.trip;
        break;
      case enumMarineInsuranceEntityType.LCL_QUOTE:
        method = 'createLCLQuoteInsurance';
        entityToCreate =
          (entity as QuoteModel).shippingRole === enumShippingRole.IMPORTER
            ? (entity as QuoteModel)?.consignee
            : (entity as QuoteModel)?.shipper;
        vesselName = (entity as QuoteModel)?.voyage?.vesselName;
        voyageNumber = (entity as QuoteModel)?.voyage?.voyageNumber;
        break;
      case enumMarineInsuranceEntityType.LCL_SHIPPING:
        method = 'createLCLShipmentInsurance';
        entityToCreate =
          (entity as LCLShipmentModel).shippingRole === enumShippingRole.IMPORTER
            ? (entity as LCLShipmentModel)?.consignee
            : (entity as LCLShipmentModel)?.shipper;
        vesselName = (entity as LCLShipmentModel)?.voyage?.vesselName;
        voyageNumber = (entity as LCLShipmentModel)?.voyage?.voyageNumber;
        break;
    }

    return this.marineInsuranceRepository[method](data.commodityId, entity?.id).pipe(
      switchMap((shipment: MarineInsuranceModel) => this.getShipment(shipment.referenceNumber)),
      switchMap((shipment: MarineInsuranceModel) =>
        combineLatest([of(shipment), this.createEntity(currentCompanyId, { id: null, ...entityToCreate })]),
      ),
      switchMap(([shipment, beneficiary]: [MarineInsuranceModel, EntityModel]) =>
        this.updateQuoteCall(data, { ...shipment, beneficiary, billNumber, vesselName, voyageNumber }),
      ),
      // sending/getting data to Anova is async, we need to do a polling based on priceUpdateTimestamp
      switchMap((shipment: MarineInsuranceModel) => {
        const lastUpdatedTimestamp: string = shipment?.priceUpdateTimestamp;

        return timer(0, DEFAULT_POLLING_TIMEOUT).pipe(
          take(DEFAULT_MAX_POLLING_RETRY),
          switchMap(() => this.getShipment(shipment.referenceNumber)),
          filter((shipmentRes: MarineInsuranceModel) => shipmentRes.priceUpdateTimestamp !== lastUpdatedTimestamp),
          first(),
        );
      }),
      switchMap((shipmentRes: MarineInsuranceModel) =>
        combineLatest([of(shipmentRes), this.getPendingBills(currentCompanyId, [shipmentRes.referenceNumber], null, null, false)]),
      ),
    );
  }

  public updateQuote(
    data: DeepPartial<MarineInsuranceQuoteFormModel>,
    marineInsurance: MarineInsuranceModel,
    currentCompanyId: number,
    skipBillsPolling?: boolean,
  ): Observable<[MarineInsuranceModel, PaymentBillItemPendingModel]> {
    const lastUpdatedTimestamp: string = marineInsurance?.priceUpdateTimestamp;

    return this.updateQuoteCall(data, marineInsurance).pipe(
      switchMap(() =>
        skipBillsPolling
          ? this.getShipment(marineInsurance.referenceNumber, false)
          : timer(0, DEFAULT_POLLING_TIMEOUT).pipe(
              take(DEFAULT_MAX_POLLING_RETRY),
              switchMap(() => this.getShipment(marineInsurance.referenceNumber, false)),
              filter((shipmentRes: MarineInsuranceModel) => shipmentRes.priceUpdateTimestamp !== lastUpdatedTimestamp),
              first(),
            ),
      ),
      switchMap((shipment: MarineInsuranceModel) =>
        combineLatest([of(shipment), this.getPendingBills(currentCompanyId, [shipment.referenceNumber], null, null, false, null, false)]),
      ),
    );
  }

  public updateBeneficiary(
    marineInsurance: MarineInsuranceModel,
    beneficiary: EntityModel,
    currentCompanyId: number,
  ): Observable<MarineInsuranceModel> {
    if (marineInsurance.beneficiary?.entityCode !== beneficiary.entityCode) {
      return this.createEntity(currentCompanyId, beneficiary).pipe(
        switchMap((beneficiaryCreated: EntityModel) => this.updateQuoteCall({ beneficiaryId: beneficiaryCreated.id }, marineInsurance)),
        switchMap((marineInsuranceUpdated: MarineInsuranceModel) =>
          combineLatest([of(marineInsuranceUpdated), this.deleteEntity(marineInsurance.beneficiary.id)]),
        ),
        map(([marineInsuranceUpdated, _]: [MarineInsuranceModel, never]) => marineInsuranceUpdated),
      );
    } else {
      return this.updateEntity(marineInsurance?.beneficiary.id, beneficiary).pipe(
        map((beneficiaryUpdated: EntityModel) => ({ ...marineInsurance, beneficiary: beneficiaryUpdated })),
      );
    }
  }

  public updateManagementBeneficiary(
    marineInsurance: MarineInsuranceManagementModel,
    beneficiary: EntityModel,
  ): Observable<MarineInsuranceManagementModel> {
    if (marineInsurance.updatedBeneficiary?.entityCode !== beneficiary.entityCode) {
      return this.createManagementEntity(marineInsurance.companyId, beneficiary).pipe(
        switchMap((beneficiaryCreated: EntityModel) =>
          this.updateQuoteManagementCall({ updatedBeneficiaryId: beneficiaryCreated.id }, marineInsurance),
        ),
      );
    } else {
      return this.updateManagementEntity(marineInsurance?.updatedBeneficiary?.id, beneficiary).pipe(
        map((beneficiaryUpdated: EntityModel) => ({ ...marineInsurance, updatedBeneficiary: beneficiaryUpdated })),
      );
    }
  }

  public getBillData(
    entity: IsfModel | EntrySummaryModel | QuoteModel | LCLShipmentModel,
    entityType: enumMarineInsuranceEntityType,
  ): BillNumberModel {
    switch (entityType) {
      case enumMarineInsuranceEntityType.ISF:
        return (entity as IsfModel)?.shipment?.code === enumIsfShipmentCode.HOUSE
          ? (entity as IsfModel)?.shipment?.houseBill
          : (entity as IsfModel)?.shipment?.masterBill;
      case enumMarineInsuranceEntityType.ENTRY_SUMMARY:
        return (entity as EntrySummaryModel)?.shipments?.[0]?.code === enumIsfShipmentCode.HOUSE
          ? (entity as EntrySummaryModel)?.shipments?.[0]?.houseBill
          : (entity as EntrySummaryModel)?.shipments?.[0]?.masterBill;
      case enumMarineInsuranceEntityType.LCL_QUOTE:
        return (entity as QuoteModel).houseBill;
      case enumMarineInsuranceEntityType.LCL_SHIPPING:
        return (entity as LCLShipmentModel).houseBill;
    }
  }

  public updateQuoteManagement(
    data: MarineInsuranceQuoteManagementFormModel,
    marineInsurance: MarineInsuranceManagementModel,
    skipBillsPolling?: boolean,
    isForceDowntimeCheck?: boolean,
  ): Observable<[MarineInsuranceManagementModel, PaymentBillItemPendingModel]> {
    const lastUpdatedTimestamp: string = marineInsurance?.priceUpdateTimestamp;

    return this.updateQuoteManagementCall(data, marineInsurance).pipe(
      switchMap(() =>
        skipBillsPolling
          ? this.getManagementShipment(marineInsurance.id)
          : timer(0, DEFAULT_POLLING_TIMEOUT).pipe(
              take(DEFAULT_MAX_POLLING_RETRY),
              switchMap(() =>
                this.getManagementShipment(marineInsurance.id).pipe(
                  switchMap((shipmentRes: MarineInsuranceManagementModel) => {
                    if (shipmentRes.isProviderWasDown && isForceDowntimeCheck) {
                      return throwError({ isProviderWasDown: true });
                    } else {
                      return of(shipmentRes);
                    }
                  }),
                ),
              ),
              filter((shipmentRes: MarineInsuranceManagementModel) => shipmentRes.priceUpdateTimestamp !== lastUpdatedTimestamp),
              first(),
            ),
      ),
      switchMap((shipment: MarineInsuranceManagementModel) =>
        combineLatest([of(shipment), this.getPendingBills(marineInsurance.companyId, [shipment.referenceNumber], null, null, false)]),
      ),
    );
  }

  private updateQuoteCall(
    data: DeepPartial<MarineInsuranceQuoteFormModel>,
    marineInsurance: MarineInsuranceModel,
  ): Observable<MarineInsuranceModel> {
    const shippingDate: string = !!data?.shippingDate ? this.dateTimezoneService.startOfTheDay(data?.shippingDate) : null;

    return this.updateShipment(
      {
        ...marineInsurance,
        manufactureDate: data?.manufactureDate || marineInsurance?.manufactureDate,
        beneficiaryId: data?.beneficiaryId || marineInsurance?.beneficiary?.id,
        shippingDate: shippingDate || marineInsurance.shippingDate,
        vesselName: data.vesselName || marineInsurance.vesselName,
        voyageNumber: data.voyageNumber || marineInsurance.voyageNumber,
        originEntityCode:
          data?.originEntityCode !== DEFAULT_EMPTY_RADIO ? data?.originEntityCode || marineInsurance.originEntityCode : null,
        originCountryId: data?.originCountry?.id || marineInsurance?.originCountry?.id,
        originCity: data.originCity || marineInsurance?.originCity,
        destinationEntityCode:
          data?.destinationEntityCode !== DEFAULT_EMPTY_RADIO ? data?.destinationEntityCode || marineInsurance.destinationEntityCode : null,
        destinationCountryId: data?.destinationCountry?.id || marineInsurance?.destinationCountry?.id,
        destinationStateId: data?.destinationState?.id || marineInsurance?.destinationState?.id,
        destinationCity: data.destinationCity || marineInsurance?.destinationCity,
        insuredValue: {
          ...marineInsurance.insuredValue,
          commodityId: data.commodityId || marineInsurance?.insuredValue?.commodity?.id,
          insuredAmount: data.insuredAmount || marineInsurance?.insuredValue?.insuredAmount,
          typeOfShipment: data.typeOfShipment || marineInsurance?.insuredValue?.typeOfShipment,
          goodsTypeDescription: data.goodsTypeDescription || marineInsurance?.insuredValue?.goodsTypeDescription,
        },
      },
      false,
    );
  }

  private updateQuoteManagementCall(
    data: DeepPartial<MarineInsuranceQuoteManagementFormModel>,
    marineInsurance: MarineInsuranceManagementModel,
  ): Observable<MarineInsuranceManagementModel> {
    const shippingDate: string = !!data?.shippingDate ? this.dateTimezoneService.startOfTheDay(data?.shippingDate) : null;
    const updatedShippingDate: string = !!data?.updatedShippingDate
      ? this.dateTimezoneService.startOfTheDay(data?.updatedShippingDate)
      : null;

    return this.updateManagementShipment({
      ...marineInsurance,
      manufactureDate: data?.manufactureDate || marineInsurance?.manufactureDate,
      updatedManufactureDate: data?.manufactureDate || marineInsurance?.updatedManufactureDate,
      beneficiaryId: data?.beneficiaryId || marineInsurance?.beneficiary?.id,
      updatedBeneficiaryId: data?.updatedBeneficiaryId || marineInsurance?.updatedBeneficiary?.id,
      shippingDate: shippingDate || marineInsurance.shippingDate,
      updatedShippingDate: updatedShippingDate || marineInsurance.updatedShippingDate,
      vesselName: data.vesselName || marineInsurance.vesselName,
      updatedVesselName: data.updatedVesselName || marineInsurance.updatedVesselName,
      voyageNumber: data.voyageNumber || marineInsurance.voyageNumber,
      updatedVoyageNumber: data.updatedVoyageNumber || marineInsurance.updatedVoyageNumber,
      originEntityCode: data?.originEntityCode || marineInsurance.originEntityCode,
      originCountryId: data?.originCountry?.id || marineInsurance?.originCountry?.id,
      updatedOriginCountryId: data?.updatedOriginCountryId || data?.updatedOriginCountry?.id || marineInsurance?.updatedOriginCountry?.id,
      originCity: data.originCity || marineInsurance?.originCity,
      updatedOriginCity: data.updatedOriginCity || marineInsurance?.updatedOriginCity,
      destinationEntityCode: data?.destinationEntityCode,
      destinationCountryId: data?.destinationCountry?.id || marineInsurance?.destinationCountry?.id,
      updatedDestinationCountryId:
        data?.updatedDestinationCountryId || data?.updatedDestinationCountry?.id || marineInsurance?.updatedDestinationCountry?.id,
      destinationStateId: data?.destinationState?.id || marineInsurance?.destinationState?.id,
      updatedDestinationStateId: data?.updatedDestinationState?.id || marineInsurance?.updatedDestinationState?.id,
      destinationCity: data.destinationCity || marineInsurance?.destinationCity,
      updatedDestinationCity: data.updatedDestinationCity || marineInsurance?.updatedDestinationCity,
      updatedTransportationMode: data.updatedTransportationMode || data.transportationMode || marineInsurance?.transportationMode,
      updatedBillNumber: {
        carrier: data?.updatedBillNumber?.carrier || marineInsurance?.billNumber?.carrier,
        number: data?.updatedBillNumber?.number || marineInsurance?.billNumber?.number,
      },
      insuredValue: {
        ...marineInsurance.insuredValue,
        commodityId: data?.insuredValue?.commodityId || marineInsurance?.insuredValue?.commodity?.id,
        insuredAmount: data?.insuredValue?.insuredAmount || marineInsurance?.insuredValue?.insuredAmount,
        typeOfShipment: data?.insuredValue?.typeOfShipment || marineInsurance?.insuredValue?.typeOfShipment,
        goodsTypeDescription: data?.insuredValue?.goodsTypeDescription || marineInsurance?.insuredValue?.goodsTypeDescription,
      },
      updatedInsuredValue: {
        ...marineInsurance.updatedInsuredValue,
        commodityId: data?.updatedInsuredValue?.commodityId || marineInsurance?.updatedInsuredValue?.commodity?.id,
        insuredAmount: data?.updatedInsuredValue?.insuredAmount || marineInsurance?.updatedInsuredValue?.insuredAmount,
        typeOfShipment:
          data?.typeOfShipment || data?.updatedInsuredValue?.typeOfShipment || marineInsurance?.updatedInsuredValue?.typeOfShipment,
        goodsTypeDescription:
          data?.goodsTypeDescription ||
          data?.updatedInsuredValue?.goodsTypeDescription ||
          marineInsurance?.updatedInsuredValue?.goodsTypeDescription,
      },
    });
  }
}
